import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, FileTextIcon, GlobeIcon, PackageIcon, ShieldCheckIcon } from 'lucide-react';
import { bonuses, checkoutUrl, offerIncluded, product } from '../data/landing';
import { isValidCheckoutUrl } from '../utils/checkout';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton } from './CtaButton';
import { SectionHeading } from './SectionHeading';
const delivery = [{
  icon: FileTextIcon,
  label: 'Digital PDF'
}, {
  icon: GlobeIcon,
  label: 'Available worldwide'
}, {
  icon: PackageIcon,
  label: 'Nothing shipped'
}];
export function OfferSection() {
  const [showNote, setShowNote] = useState(false);
  const valid = isValidCheckoutUrl();
  const sectionRef = useRef<HTMLElement>(null);
  const pixelParams = {
    content_name: product.name,
    value: Number(product.price),
    currency: product.currency
  };
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        trackPixel('ViewContent', pixelParams);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleCheckout = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (valid) {
      trackPixel('InitiateCheckout', pixelParams);
      return;
    }
    event.preventDefault();
    setShowNote(true);
  };
  return <section ref={sectionRef} id="offer-details" className="bg-teal-tint py-12 md:py-24" data-id="element-112" data-mp="1ckwrpfdvmqj5">
      <div className="mx-auto max-w-[1180px] px-5 md:px-8" data-id="element-113" data-mp="17n4se0awk24x">
        <SectionHeading eyebrow="Everything in one pack" title="Your next 12 meetings," highlight="ready to lead." data-id="element-114" data-mp="17oxx3a1ygcaa" />

        <div className="mx-auto max-w-[600px] rounded-xl border border-teal/20 bg-white px-5 py-8 shadow-[0_24px_60px_rgba(20,33,61,0.1)] sm:px-10 sm:py-10" data-id="element-115" data-mp="3sd3u3s1ctez3">
          <div className="text-center" data-id="element-116" data-mp="3tusqommujxv9">
            <span className="inline-block rounded-md bg-navy px-3 py-1.5 text-[11px] font-bold uppercase tracking-[2px] text-amber" data-id="element-117" data-mp="2ng8q5e53gea">
              {product.volume} + all 5 bonuses
            </span>
            <img src={product.mockup} alt="Youth Ministry Ready box set with the 5 bonus books" width={1024} height={1536} className="mx-auto mt-8 h-auto w-full max-w-[280px] rounded-xl shadow-[0_20px_40px_rgba(20,33,61,0.2)]" data-id="element-118" data-mp="3oumtkqxt5j5s" />
            <h3 className="mt-8 font-display text-[38px] font-extrabold uppercase leading-none text-navy" data-id="element-119" data-mp="315u5fgnfgflq">{product.name}</h3>
            <p className="mt-2 text-muted" data-id="element-120" data-mp="3656so2i7noou">{product.series} · 12 complete meetings + 5 bonus resources</p>
          </div>

          <div className="mt-8 border-t border-navy/10 pt-7" data-id="element-121" data-mp="2s5fikcutesg4">
            <p className="text-center text-xs font-bold uppercase tracking-[2.5px] text-navy" data-id="element-122" data-mp="2x409tms3a1sf">What's included</p>
            <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-123" data-mp="2diqa3lil54xl">
              {offerIncluded.map(item => <OfferItem key={item} data-id="element-124" data-mp="28kyjqvxl9qvi">{item}</OfferItem>)}
            </ul>
            <p className="mt-7 text-center text-xs font-bold uppercase tracking-[2.5px] text-navy" data-id="element-125" data-mp="2mjy802h6w3r5">The 5 bonuses</p>
            <ul className="mt-5 space-y-3" data-id="element-126" data-mp="2ij4gkz4jg6ti">
              {bonuses.map(bonus => <OfferItem key={bonus.shortTitle} data-id="element-127" data-mp="2weykt4uda80a">
                  {bonus.shortTitle} — {bonus.subtitle}
                </OfferItem>)}
            </ul>
          </div>

          <div className="mt-8 border-t border-navy/10 pt-8 text-center" data-id="element-128" data-mp="3662x1wsnrerf">
            <div className="flex items-start justify-center font-display font-extrabold text-navy" data-id="element-129" data-mp="3179is25jrjq7">
              <span className="mt-4 text-[40px] leading-none" data-id="element-130" data-mp="3f6iv2qqmttow">$</span>
              <span className="text-[104px] leading-[0.85]" data-id="element-131" data-mp="3a87lmh491l77">{product.price}</span>
              <span className="ml-2 self-end font-sans text-sm font-semibold text-muted" data-id="element-132" data-mp="3a6d133ww3v3n">{product.currency}</span>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs font-bold uppercase tracking-[2px] text-muted" data-id="element-133" data-mp="1dhlepxnw272h">
              <span data-id="element-134" data-mp="1i9d9kke2uyyf">One-time payment</span>
              <span data-id="element-135" data-mp="1maf8t17zvb0e">No subscription</span>
            </div>

            <CtaButton href={valid ? checkoutUrl : '#checkout-note'} onClick={handleCheckout} className="mt-7 w-full" data-id="element-136" data-mp="1m9x7vrae08m5">
              {product.ctaLabel} now
            </CtaButton>

            <div id="checkout-note" role="status" aria-live="polite" data-id="element-137" data-mp="ukakwsl105uf">
              <AnimatePresence data-id="element-138" data-mp="ykcleyf0yt84">
                {showNote && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-5 rounded-md border border-amber bg-amber-cream p-3 text-sm text-navy" data-id="element-139" data-mp="ymytcz5tvaqv">
                    Purchase access will be available when this store launches.
                  </motion.p>}
              </AnimatePresence>
            </div>

            <p className="mt-5 flex items-center justify-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-140" data-mp="2fc6cmj1nnz4c">
              <ShieldCheckIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-141" data-mp="2bauwd44o8q8t" />
              10-day money-back guarantee · No questions asked
            </p>
          </div>

          <ul className="mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 border-t border-navy/10 pt-6 text-[13px] font-medium text-muted" data-id="element-142" data-mp="21d6yw6fol4my">
            {delivery.map(({
            icon: Icon,
            label
          }) => <li key={label} className="flex items-center gap-1.5" data-id="element-143" data-mp="1sdjx3jj1hwtd">
                <Icon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-144" data-mp="3qsegqowqjii4" />
                {label}
              </li>)}
          </ul>
        </div>
        <p className="mt-5 text-center text-[13px] text-muted" data-id="element-145" data-mp="3mrb2g6uc5uj0">Price shown in USD.</p>
      </div>
    </section>;
}
function OfferItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-2.5 text-[15px] font-medium text-navy" data-id="element-146" data-mp="1ovmew0922rem">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-cream" data-id="element-147" data-mp="1b490t1tx2lqz">
        <CheckIcon className="h-3 w-3 text-amber-dark" strokeWidth={3} aria-hidden="true" data-id="element-148" data-mp="1f5uid0hebo7i" />
      </span>
      {children}
    </li>;
}